import { faClock, faPlus, faTrash, faTrashAlt, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGMutualFundDetailsForm } from ".";
import { MFAssetsTypes, MFAssetsTypesOptions } from "../../../config/filingConstants";
import { IFormITR1, IMutualFundForm, IMutualFundFormTypes } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { AlertModal, ConfirmationModal } from "../../modals";

const CGMutualFundDetails: React.FC = () => {
  const [showCommingSoon, setshowCommingSoon] = useState<boolean>(false);
  const [showAddMFCanvas, setShowAddMFCanvas] = useState<IMutualFundFormTypes>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<IMutualFundForm | null>(null);
  const [activeItem, setActiveItem] = useState<IMutualFundForm | null>(null);

  // used in both edit and delete cases
  const [activeItemIdx, setActiveItemIdx] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
  const MutualFundDestails = cloneDeep(values.IncomeDetails?.MutualFunds || []);

  const handleDeleteInterestItem = async () => {
    MutualFundDestails.splice(Number(activeItemIdx), 1);
    setFieldValue("IncomeDetails.MutualFunds", MutualFundDestails);
    setShowDeleteModal(() => null);
    setActiveItem(() => null);
    setActiveItemIdx(() => "");
    handleTotalCalculation();
  };

  const handleTotalCalculation = () => {
    let TotalListedLong = 0;
    let TotalListedShort = 0;
    let TotalUnlistedLong = 0;
    let TotalUnlistedShort = 0;
    for (let index = 0; index < MutualFundDestails.length; index++) {
      const stock = MutualFundDestails[index];
      if (stock.TypeAssets === MFAssetsTypes.Listed_Long_10_Per) {
        TotalListedLong += Number(stock.CapitalGains);
      } else if (stock.TypeAssets === MFAssetsTypes.Listed_Short_15_Per) {
        TotalListedShort += Number(stock.CapitalGains);
      } else if (stock.TypeAssets === MFAssetsTypes.Unlisted_Normal) {
        TotalUnlistedLong += Number(stock.CapitalGains);
      } else if (stock.TypeAssets === MFAssetsTypes.Unlisted_Short_20_Per) {
        TotalUnlistedShort += Number(stock.CapitalGains);
      }
    }

    let _TotalSplRateTax = 0;
    let _TotalNrlRateIncome = 0;
    // in excess of 1 lakh
    if (TotalListedLong > 100000) {
      const excessAmount = TotalListedLong - 100000;
      _TotalSplRateTax += excessAmount * 0.1;
    }

    _TotalSplRateTax += TotalListedShort * 0.15;

    _TotalSplRateTax += TotalUnlistedShort * 0.2;

    _TotalNrlRateIncome = TotalUnlistedLong;

    const MutualFundsGains =
      MutualFundDestails?.reduce((acc, curr) => {
        const value = parseInt(curr.CapitalGains);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const CapitalGains = {
      ...values.CapitalGains,
      TotalMutualFundsGain: MutualFundsGains
    };

    const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      CapitalGains: CapitalGains,
      TotalCapitalGains: TotalCapitalGains
    };

    const CapitalGainsSplRateTax = {
      ...values.CapitalGainsSplRateTax,
      MutualFndSplRateTax: _TotalSplRateTax
    };

    const CapitalGainsNrlRateIncome = {
      ...values.CapitalGainsNrlRateIncome,
      MutualFndNrlRateIncome: _TotalNrlRateIncome
    };

    const TotalCGSplRtTax = Object.values(CapitalGainsSplRateTax)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const TotalCGNrlRtInc = Object.values(CapitalGainsNrlRateIncome)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const SPL_DATA = {
      CapitalGainsSplRateTax: { ...CapitalGainsSplRateTax },
      CapitalGainsNrlRateIncome: { ...CapitalGainsNrlRateIncome },
      TotalSplRateTax: TotalCGSplRtTax,
      TotalNrlRateIncome: TotalCGNrlRtInc
    };

    setValues({ ...cloneDeep(values), ...DATA, ...SPL_DATA });
    setFieldValue("IncomeDetails.MutualFunds", MutualFundDestails);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>Mutual Fund</h5>
          <PreventEditComponent>
            <div className="btns-wrap">
              <Button
                variant="primaryDark"
                size="sm"
                // onClick={() => setShowAddMFCanvas(() => "UPLOAD")}
                onClick={() => setshowCommingSoon(true)}
              >
                <FASvgIcon icon={faUpload} />
                Upload
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddMFCanvas(() => "MANUAL")}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            </div>
          </PreventEditComponent>
        </div>

        <dl className="eds-list eds-dl eds-inline-between">
          {MutualFundDestails.map((mutualFund, index) => {
            return (
              <div key={`any-asset-${index}`} className="eds-list--item">
                <dt>
                  {MFAssetsTypesOptions[mutualFund.TypeAssets]} ({mutualFund.DescriptionAssetSold})
                </dt>
                <dd>
                  <span>
                    <PriceDisplay price={mutualFund?.CapitalGains} />
                  </span>
                  <Button
                    variant="link"
                    onClick={() => {
                      setShowAddMFCanvas(() => "MANUAL");
                      setActiveItem(() => mutualFund);
                      setActiveItemIdx(() => String(index));
                    }}
                  >
                    View
                    <PreventEditComponent>
                      <>/Edit</>
                    </PreventEditComponent>
                  </Button>
                  <PreventEditComponent>
                    <Button
                      variant="transparent"
                      className="icon-only"
                      onClick={() => {
                        setShowDeleteModal(() => mutualFund);
                        setActiveItemIdx(() => String(index));
                      }}
                    >
                      <FASvgIcon icon={faTrash} />
                    </Button>
                  </PreventEditComponent>
                </dd>
              </div>
            );
          })}
        </dl>
      </section>

      <CGMutualFundDetailsForm
        showCanvas={!!showAddMFCanvas}
        formType={showAddMFCanvas}
        handleCloseBtnPress={(mutualFundData) => {
          if (mutualFundData) {
            if (activeItem) {
              MutualFundDestails.splice(Number(activeItemIdx), 1, mutualFundData);
            } else {
              MutualFundDestails.push(mutualFundData);
            }
            handleTotalCalculation();
          }
          setShowAddMFCanvas(() => null);
          setActiveItem(() => null);
          setActiveItemIdx(() => "");
        }}
        formValues={activeItem}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={
          <p>
            {showDeleteModal ? (
              <>
                You want to delete {MFAssetsTypesOptions[showDeleteModal.TypeAssets]} (
                {showDeleteModal.DescriptionAssetSold})
              </>
            ) : null}
          </p>
        }
        onYesBtnClick={() => handleDeleteInterestItem()}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />

      {showCommingSoon && (
        <AlertModal
          showModal={showCommingSoon}
          iconColorClass="text-warning"
          title="Coming Soon"
          message={<p></p>}
          icon={faClock}
          onYesBtnClick={() => setshowCommingSoon(false)}
          onYesBtnText="Okay"
        />
      )}
    </>
  );
};

export default CGMutualFundDetails;
